import { Controller } from "@hotwired/stimulus"

// The dev tools widget (DevTools::WidgetComponent): the launcher with its count of new captures,
// and the panel that frames a dev tools page. Each capture arrives over Turbo Streams as a hidden
// signal element. With the panel closed it bumps the count; with it open, it reloads the tool's
// list page if that is what the panel shows. Opening the panel with new captures shows that list.
// Expanded, the panel is wide enough for the tool inside to get its wide-screen layout. Whether the
// panel is open or expanded, the selected tool and page, and the count are kept per browser in
// localStorage, so they carry over between page loads.
const STORAGE_KEY = 'devToolsWidget'
const LIST_REFRESH_DELAY_MS = 400

export default class extends Controller {
  static targets = ['capture', 'panel', 'frame', 'launcher', 'expand', 'count', 'inlineCount', 'context', 'contextStrip', 'tab']
  static values = { tools: Array }

  connect() {
    const { open, expanded, tool, url, count } = this.load()
    this.state = { open: Boolean(open), expanded: Boolean(expanded), tool, url: url || null, count: count || 0 }
    if (!this.toolsValue.some((candidate) => candidate.key === this.state.tool)) this.state.tool = this.toolsValue[0]?.key

    this.refreshContext = this.refreshContext.bind(this)
    document.addEventListener('turbo:load', this.refreshContext)
    this.refreshContext()
    this.render()
    if (this.state.open) this.loadFrame(this.state.url || this.toolUrl())
  }

  disconnect() {
    document.removeEventListener('turbo:load', this.refreshContext)
    clearTimeout(this.listRefreshTimer)
  }

  captureTargetConnected(signal) {
    signal.remove()

    if (this.state.open) {
      this.scheduleListRefresh()
      return
    }

    this.state.count += 1
    this.save()
    this.render()
  }

  toggle() {
    this.state.open ? this.close() : this.open()
  }

  open() {
    const hasNewCaptures = this.state.count > 0
    this.state.open = true
    this.state.count = 0
    this.save()
    this.render()

    if (hasNewCaptures && this.currentTool()?.countsCaptures) {
      this.loadFrame(this.toolUrl(), { reload: true })
    } else {
      this.loadFrame(this.state.url || this.toolUrl())
    }
  }

  close() {
    this.state.open = false
    this.save()
    this.render()
  }

  toggleExpanded() {
    this.state.expanded = !this.state.expanded
    this.save()
    this.render()
  }

  selectTool({ params }) {
    this.state.tool = params.key
    this.state.url = null
    this.save()
    this.render()
    this.loadFrame(this.toolUrl())
  }

  openInNewTab(event) {
    event.preventDefault()
    window.open(this.currentFrameUrl() || this.toolUrl(), '_blank', 'noopener')
  }

  frameLoaded() {
    const url = this.currentFrameUrl()
    if (!url) return

    this.state.url = url
    this.save()
  }

  refreshContext() {
    const source = document.getElementById('dev-tools-page-context')
    const accountName = source?.dataset.accountName
    this.pageContext = { account_id: source?.dataset.accountId, path: window.location.pathname }
    if (this.hasContextTarget) {
      this.contextTarget.textContent = [accountName, window.location.pathname].filter(Boolean).join(' · ')
    }
  }

  // private

  render() {
    this.panelTarget.classList.toggle('d-none', !this.state.open)
    this.panelTarget.classList.toggle('dev-tools-panel-expanded', this.state.expanded)
    this.expandTarget.setAttribute('aria-pressed', String(this.state.expanded))
    this.launcherTarget.setAttribute('aria-expanded', String(this.state.open))

    const countText = this.state.count > 99 ? '99+' : String(this.state.count)
    this.countTarget.classList.toggle('d-none', this.state.count === 0)
    this.countTarget.textContent = countText
    this.countTarget.setAttribute('aria-label', `${this.state.count} new captured messages`)
    this.inlineCountTarget.classList.toggle('d-none', this.state.count === 0)
    this.inlineCountTarget.firstElementChild.textContent = countText

    this.tabTargets.forEach((tab) => {
      const selected = tab.dataset.key === this.state.tool
      tab.classList.toggle('active', selected)
      tab.setAttribute('aria-selected', String(selected))
    })
    if (this.hasContextStripTarget) this.contextStripTarget.classList.toggle('d-none', !this.currentTool()?.usesPageContext)
  }

  scheduleListRefresh() {
    clearTimeout(this.listRefreshTimer)
    this.listRefreshTimer = setTimeout(() => this.refreshListIfShown(), LIST_REFRESH_DELAY_MS)
  }

  refreshListIfShown() {
    const tool = this.currentTool()
    const current = this.currentFrameUrl()
    if (!tool?.countsCaptures || !current) return

    const listPath = new URL(tool.url, window.location.origin).pathname
    if (new URL(current, window.location.origin).pathname === listPath) this.frameTarget.contentWindow.location.reload()
  }

  loadFrame(url, { reload = false } = {}) {
    if (!url) return

    if (this.currentFrameUrl() !== url) {
      this.frameTarget.src = url
    } else if (reload) {
      this.frameTarget.contentWindow.location.reload()
    }
  }

  currentFrameUrl() {
    try {
      const location = this.frameTarget.contentWindow?.location
      if (!location || location.href === 'about:blank') return null

      return location.pathname + location.search
    } catch {
      return null
    }
  }

  currentTool() {
    return this.toolsValue.find((tool) => tool.key === this.state.tool) || this.toolsValue[0]
  }

  toolUrl() {
    const tool = this.currentTool()
    if (!tool) return null

    const url = new URL(tool.url, window.location.origin)
    if (tool.usesPageContext) {
      Object.entries(this.pageContext || {}).forEach(([key, value]) => {
        if (value) url.searchParams.set(`context[${key}]`, value)
      })
    }
    return url.pathname + url.search
  }

  load() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}
    } catch {
      return {}
    }
  }

  save() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state))
    } catch {
      // Storage can be unavailable (private windows, blocked site data); the widget still works for this page.
    }
  }
}
